import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AGE_OF_MAJORITY, ATTRIBUTE_KEYS, COUNCIL_SEAT_DEFS, EDUCATION_START_AGE, RANK_NAMES, RANK_ORDER, SCHEMES, SQUIRE_MAX_START_AGE, SQUIRE_START_AGE, SQUIRE_XP_MAX, TRAIT_DEFS,
  absHourOf, formatDateTime, formatDate,
} from '@pk/shared';
import type { AttributeKey, CharacterView, EducationFocus, HouseView, Id, ServiceCandidate, SquireCandidate, TitleRank, TitleSummary } from '@pk/shared';
import {
  actorOf, useCharacter, useCharacterHistory, useCommand, useHouse, useMarriageCandidates, useServiceCandidates, useSquireCandidates, useTitle, useTitleIndex, useWorld,
} from '../api/hooks';
import { Bar, Btn, Collapsible, ConfirmRow, Empty, ErrorBox, Field, Inline, Loading, Panel, Screen, ScreenTop, ScrollArea, Segmented, Sheet, Stat, Tag } from '../components/ui';
import { Chips, FilterBar, PickRow, VerdictChip, moodOf } from '../components/picker';
import { CharLink, CharPortrait, HouseBadge, TitleLink, TraitTags } from '../components/game';
import { IconBook, IconCrown, IconDagger, IconShield, IconTree, IconUsers, IconScroll, IconHourglass, ResPrestige, ResourceIcon } from '../components/icons';
import { squireEligible } from '../lib/houseActions';
import { PerksBlock, RenownBlock } from '../components/Perks';
import { TenureRows } from './Title';
import '../styles/screens-people.css';

type CharTab = 'attributes' | 'traits' | 'family' | 'station' | 'history';
const FOCI: EducationFocus[] = ['martial', 'diplomacy', 'stewardship', 'intrigue', 'learning'];

export function CharacterScreen() {
  const { id } = useParams();
  const { t } = useTranslation();
  const ch = useCharacter(id);
  const house = useHouse();
  const world = useWorld();
  const [tab, setTab] = useState<CharTab>('attributes');

  if (ch.isLoading) return <Loading />;
  if (ch.isError || !ch.data) return <ErrorBox error={ch.error} onRetry={() => ch.refetch()} />;
  const c = ch.data;
  const w = world.data;
  const traitList = c.traits.map((k) => TRAIT_DEFS[k]).filter(Boolean);
  const birth = formatDate(c.birthDay);
  const h = house.data;
  const own = c.controllable && c.alive && !!h;
  const foreign = !c.controllable && c.alive && !!h && h.house.id !== c.houseId;

  return (
    <Screen className="pp-page">
      <ScreenTop>
      <div className={`hero char-hero ${c.alive ? '' : 'dead'}`}>
        <div className="ch-portrait"><CharPortrait c={c} size={72} /></div>
        <div className="ch-body">
          <h1>{c.firstName}</h1>
          <div className="ch-house">
            {c.houseId
              ? <Link className="link" to={`/tree/${c.houseId}`} style={{ borderBottom: 0 }}><HouseBadge houseId={c.houseId} name={c.houseName ? t('picker.house', { name: c.houseName }) : null} /></Link>
              : null}
          </div>
          <div className="ch-sub">
            {c.alive ? t('common.age', { age: c.age }) : t('character.diedAt', { age: c.age })}{c.primaryTitle ? ` · ${c.primaryTitle}` : ''}
            {c.newHouse && <> <Tag tone="green" title={t('newHouse.hint')}>{t('newHouse.badge')}</Tag></>}
          </div>
        </div>
      </div>
      <div className="char-tabs">
        <Segmented<CharTab> value={tab} onChange={setTab} options={[
          { value: 'attributes', label: t('character.attributes') },
          { value: 'traits', label: t('character.traits') },
          { value: 'family', label: t('character.family') },
          { value: 'station', label: t('character.station') },
          { value: 'history', label: t('history.section') },
        ]} />
      </div>
      </ScreenTop>

      <ScrollArea>
      <div className="ch-extra">
          <div className="ch-tags">
            {c.isHead && <Tag tone="gold">&#9733; {t('character.head')}</Tag>}
            {!c.alive && <Tag tone="red">{c.deathCause ?? t('character.dead')}</Tag>}
            {c.pregnant === true && (
              <Tag tone="blue">{c.pregnancy ? t('character.pregnantDue', { date: formatDateTime(c.pregnancy.dueHour ?? absHourOf(c.pregnancy.dueDay)) }) : t('character.pregnant')}</Tag>
            )}
            {c.prisonerOf && <Tag tone="red">{t('character.prisoner')}</Tag>}
            {c.commandingArmyId && <Tag tone="green">{t('character.commanding')}</Tag>}
            {c.isAI && <Tag>{t('character.npc')}</Tag>}
          </div>
          {traitList.length > 0 && <div className="ch-keytraits"><TraitTags traits={c.traits} max={4} /></div>}
          <div className="hero-meta">
            <Stat icon={<ResPrestige />} label={t('character.prestige')} value={Math.floor(c.prestige)} />
            {c.health !== null
              ? <Stat label={t('character.health')} value={Math.round(c.health * 10) / 10} trend={c.health < 4 ? 'neg' : 'pos'} />
              : <Stat label={t('character.health')} value={t(`character.healthStatus.${c.healthStatus}`)} />}
          </div>
      </div>

      {(own || foreign) && h && (
        <Panel tone={foreign ? undefined : 'gold'} icon={foreign ? <IconDagger /> : <IconCrown />} title={t('character.actions')}>
          {own && <OwnActions c={c} house={h} />}
          {foreign && <SchemeAction c={c} house={h} />}
          {c.squireOf && <DismissSquire c={c} house={h} />}
        </Panel>
      )}

      {tab === 'attributes' && (
        <Panel icon={<IconShield />} title={t('character.attributes')}>
          {c.skills
            ? <SkillRows c={c} house={own ? h : undefined} />
            : <div className="attrs">
              {ATTRIBUTE_KEYS.map((k) => <Bar key={k} label={t(`attr.${k}`)} value={c.attributes[k]} max={20} />)}
            </div>}
          <div className="char-facts">
            {c.health !== null
              ? <Bar label={t('character.health')} value={Math.round(c.health * 10) / 10} max={10} tone={c.health < 4 ? 'red' : 'green'} />
              : <div className="kv"><span>{t('character.health')}</span><span>{t(`character.healthStatus.${c.healthStatus}`)}</span></div>}
            <div className="kv"><span>{t('character.prestige')}</span><span className="num">{Math.floor(c.prestige)}</span></div>
            {c.opinionOfLiege !== null && <div className="kv"><span>{t('character.opinionLiege')}</span><span className="num">{c.opinionOfLiege > 0 ? '+' : ''}{c.opinionOfLiege}</span></div>}
            <div className="kv"><span>{t('character.born')}</span><span>{birth}</span></div>
            {c.education && <div className="kv"><span>{t('character.education')}</span><span>{t(`attr.${c.education.focus}`)}{c.education.tutorId && <> · <CharLink id={c.education.tutorId} /></>}</span></div>}
          </div>
        </Panel>
      )}

      {tab === 'attributes' && (c.renown !== undefined || (c.perks?.length ?? 0) > 0 || (c.pendingPerks?.length ?? 0) > 0) && (
        <Panel icon={<IconCrown />} title={t('perks.title')} tone={(c.pendingPerks?.length ?? 0) > 0 ? 'gold' : undefined}>
          <RenownBlock c={c} />
          <PerksBlock c={c} house={own ? h : undefined} />
        </Panel>
      )}

      {tab === 'traits' && (
        <Panel icon={<IconScroll />} title={t('character.traits')}>
          {traitList.length === 0 ? <Empty>{t('character.noTraits')}</Empty> : (
            <div className="ch-traits">
              {traitList.map((d) => {
                const tone = d.category === 'congenital' ? 'blue' : d.category === 'acquired' ? 'red' : d.category === 'education' ? 'green' : 'plain';
                return (
                  <div key={d.key} className={`ch-trait ${tone}`}>
                    <div className="ch-trait-head"><b>{d.name}</b><span className="muted small">{t(`traitCat.${d.category}`)}</span></div>
                    <p>{d.description}{d.attributes ? ' ' + Object.entries(d.attributes).map(([k, v]) => `${t(`attr.${k}`)} ${v! > 0 ? '+' : ''}${v}`).join(', ') : ''}</p>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      )}

      {tab === 'family' && (
        <Panel icon={<IconUsers />} title={t('character.family')}>
          <div className="rel-list">
            <div className="kv"><span>{t('character.father')}</span><CharLink id={c.fatherId} /></div>
            <div className="kv"><span>{t('character.mother')}</span><CharLink id={c.motherId} /></div>
            <div className="kv"><span>{c.sex === 'M' ? t('character.wife') : t('character.husband')}</span><CharLink id={c.spouseId} /></div>
            {c.betrothedId && <div className="kv"><span>{t('character.betrothed')}</span><CharLink id={c.betrothedId} /></div>}
            <div className="kv"><span>{t('character.children')}</span>
              <span>{c.childIds.length === 0 ? t('common.none') : c.childIds.map((cid, i) => <span key={cid}>{i > 0 && ', '}<CharLink id={cid} /></span>)}</span>
            </div>
          </div>
          {c.houseId && <div style={{ marginTop: 14 }}><Link className="btn small quiet" to={`/tree/${c.houseId}`}><IconTree />{t('character.houseTree')}</Link></div>}
        </Panel>
      )}

      {tab === 'station' && (
        <Panel icon={<IconCrown />} title={t('character.station')}>
          <div className="rel-list">
            <div className="kv"><span>{t('character.titles')}</span>
              <span>{c.heldTitleIds.length === 0 ? t('common.none') : c.heldTitleIds.map((tid, i) => <span key={tid}>{i > 0 && ', '}<TitleLink id={tid} /></span>)}</span>
            </div>
            <div className="kv"><span>{t('character.council')}</span>
              <span>{c.councilSeats.length === 0 ? t('common.none') : c.councilSeats.map((s, i) => (
                <span key={s.titleId + s.seat}>{i > 0 && ', '}{COUNCIL_SEAT_DEFS[s.seat].name} <span className="muted">{t('common.of')}</span> <TitleLink id={s.titleId} /></span>
              ))}</span>
            </div>
            <div className="kv"><span>{t('character.knightOf')}</span>{c.knightOf ? <TitleLink id={c.knightOf} /> : <span className="muted">{t('common.none')}</span>}</div>
            <div className="kv"><span>{t('character.squireOf')}</span>{c.squireOf ? <TitleLink id={c.squireOf} /> : <span className="muted">{t('common.none')}</span>}</div>
            <div className="kv"><span>{t('character.location')}</span><TitleLink id={c.locationId} /></div>
            {c.claimTitleIds.length > 0 && <div className="kv"><span>{t('character.claims')}</span><span>{c.claimTitleIds.map((tid, i) => <span key={tid}>{i > 0 && ', '}<TitleLink id={tid} /></span>)}</span></div>}
            {c.prisonerOf && <div className="kv"><span>{t('character.prisonerOf')}</span><TitleLink id={c.prisonerOf} /></div>}
            {w && c.commandingArmyId && <div className="kv"><span>{t('character.army')}</span><Link className="link" to="/map?view=military">{t('character.viewArmy')}</Link></div>}
          </div>
          <ChivalrySection c={c} />
        </Panel>
      )}

      {tab === 'history' && <HistorySection id={c.id} houseId={c.houseId} />}
      </ScrollArea>
    </Screen>
  );
}

// ------------------------------------------------------------------ history

function HistorySection({ id, houseId }: { id: Id; houseId: Id | null }) {
  const { t } = useTranslation();
  const history = useCharacterHistory(id);
  const entries = history.data?.chronicle ?? [];
  return (
    <Panel icon={<IconBook />} title={t('history.section')} right={
      houseId ? <Link className="link" to={`/tree/${houseId}`}>{t('character.houseTree')}</Link> : null
    }>
      {history.isLoading && <Loading />}
      {history.isError && <ErrorBox error={history.error} onRetry={() => history.refetch()} />}
      {history.data && (
        <>
          <h3 className="hist-sub">{t('history.titles')}</h3>
          {history.data.titles.length === 0 ? <Empty>{t('common.none')}</Empty> : <TenureRows tenures={history.data.titles} />}
          <h3 className="hist-sub">{t('history.chronicle')}</h3>
          {entries.length === 0 ? <Empty>{t('history.noChronicle')}</Empty> : (
            <ol className="tl compact">
              {entries.map((e) => (
                <li key={e.id} className={`tl-item ${e.important ? 'important' : ''}`}>
                  <div className="tl-meta">
                    <span className="tl-when">{formatDate(e.day)}</span>
                    <span className="tl-kind">{e.important && <span aria-hidden="true">&#9670; </span>}{t(`chronicle.kind.${e.kind}`)}</span>
                  </div>
                  <p className="tl-text">{e.text}</p>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------------ actions on other houses

function SchemeAction({ c, house }: { c: CharacterView; house: HouseView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const scheming = house.schemes.length > 0;
  const schemes = [
    { type: 'murder' as const, label: t('scheme.start', { cost: SCHEMES.murder.cost }) },
    { type: 'abduction' as const, label: t('scheme.startAbduct', { cost: SCHEMES.abduction.cost }) },
    { type: 'spread_rumours' as const, label: t('scheme.startRumours', { cost: SCHEMES.spread_rumours.cost }) },
    { type: 'befriend' as const, label: t('scheme.startBefriend', { cost: SCHEMES.befriend.cost }) },
    ...(c.isHead ? [{ type: 'steal_funds' as const, label: t('scheme.startSteal', { cost: SCHEMES.steal_funds.cost }) }] : []),
  ];
  return (
    <div className="act-group">
      <p className="act-hint">{t('character.actionsOtherHint', { name: c.firstName })}</p>
      {!scheming && (
        <div className="act-buttons">
          {schemes.map((x) => (
            <Btn key={x.type} variant="danger" icon={<IconDagger />} busy={cmd.pending}
              onClick={() => void cmd.run({ type: 'start_scheme', actorId: actorOf(house), targetId: c.id, schemeType: x.type })}>{x.label}</Btn>
          ))}
        </div>
      )}
      {scheming && (
        <Collapsible title={t('ui.notAvailable')} summary={schemes.length} className="flush">
          <ul className="list">
            {schemes.map((x) => <li key={x.type}><div className="grow"><b>{x.label}</b><span className="lr-sub">{t('scheme.already')}</span></div></li>)}
          </ul>
        </Collapsible>
      )}
      <Inline error={cmd.error} />
    </div>
  );
}

// ------------------------------------------------------------------ actions on own family

type Dialog = null | 'marry' | 'edu' | 'service' | 'squire' | 'guard' | 'leave' | 'head';

/** Own-house skills: value, a progress bar to the next training rank, the focus marked, and (adults) a focus picker. */
function SkillRows({ c, house }: { c: CharacterView; house?: HouseView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const world = useWorld();
  const day = world.data?.day ?? 0;
  const adult = c.age >= AGE_OF_MAJORITY;
  const canPick = !!house && adult && c.alive;
  const setFocus = async (focus: AttributeKey) => {
    if (!house || focus === c.focus) return;
    await cmd.run({ type: 'set_focus', actorId: actorOf(house), charId: c.id, focus });
  };
  return (
    <div className="skills">
      <div className="attrs">
        {(c.skills ?? []).map((s) => {
          const isFocus = c.focus === s.key;
          const span = Math.max(1, s.xpNextRank - s.xpRankStart);
          const pct = s.rank >= 10 ? 100 : Math.max(0, Math.min(100, ((s.xp - s.xpRankStart) / span) * 100));
          return (
            <div key={s.key} className={`skill-row${isFocus ? ' focus' : ''}`}>
              <Bar
                label={<>{t(`attr.${s.key}`)}{isFocus && <span className="skill-star" title={t('skills.focusMark')} aria-label={t('skills.focusMark')}> ★</span>}</>}
                value={s.value} max={20} tone={isFocus ? 'blue' : 'gold'} />
              <div className="skill-sub small muted">
                <span className="bar skill-xp" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemax={100} aria-label={t('skills.toNext')}>
                  <span className="bar-fill green" style={{ width: `${pct}%` }} />
                </span>
                <span className="num">
                  {t('skills.detail', { rank: s.rank, apt: s.aptitude })}
                  {s.rank < 10 ? ` · ${t('skills.xpToNext', { xp: Math.ceil(s.xpToNext) })}` : ` · ${t('skills.maxRank')}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {adult && c.focus && (
        <p className="small muted skill-focus-note">
          {c.focusActive ? t('skills.focusActive') : t('skills.focusSuspended', { date: formatDate(c.focusReadyDay ?? day) })}
        </p>
      )}
      {canPick && (
        <Field label={t('skills.focus')} hint={t('skills.focusHint')}>
          <select className="input" value={c.focus ?? ''} disabled={cmd.pending} onChange={(e) => setFocus(e.target.value as AttributeKey)}>
            {!c.focus && <option value="">—</option>}
            {ATTRIBUTE_KEYS.map((k) => <option key={k} value={k}>{t(`attr.${k}`)}</option>)}
          </select>
          <Inline error={cmd.error} />
        </Field>
      )}
    </div>
  );
}

function OwnActions({ c, house }: { c: CharacterView; house: HouseView }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const wanted = params.get('act');
  const cmd = useCommand();
  const actor = actorOf(house);
  const adult = c.age >= AGE_OF_MAJORITY;
  const head = house.members.find((m) => m.id === house.house.headId);
  const headLandless = !head || head.heldTitleIds.length === 0;
  const close = () => { setDlg(null); cmd.clearError(); };

  const canMarry = adult && !c.spouseId && !c.betrothedId;
  const canEdu = c.age >= EDUCATION_START_AGE && c.age < AGE_OF_MAJORITY;
  const canServe = adult && c.dubbed && !c.knightOf && !c.squireOf && c.heldTitleIds.length === 0 && !c.isHead;
  const canHead = adult && !c.isHead && headLandless;
  const canSquire = squireEligible(c, house) && !c.squireOf;
  const squireWhy = canSquire ? null : c.age < SQUIRE_START_AGE ? t('character.why.squireAge', { age: SQUIRE_START_AGE })
    : c.dubbed ? t('character.why.squireDubbed') : c.squireXp === null && c.age > SQUIRE_MAX_START_AGE ? t('character.why.squireTooOld', { age: SQUIRE_MAX_START_AGE }) : c.knightOf ? t('character.why.knight') : c.isHead ? t('character.why.isHead')
    : c.squireOf ? t('character.why.squire') : c.heldTitleIds.length > 0 ? t('character.why.squireTitles') : t('character.prisoner');

  const marryWhy = !adult ? t('character.why.minor', { age: AGE_OF_MAJORITY }) : c.spouseId ? t('character.why.married') : c.betrothedId ? t('character.why.betrothed') : null;
  const eduWhy = canEdu ? null : t('character.why.notChild', { from: EDUCATION_START_AGE, to: AGE_OF_MAJORITY - 1 });
  const serveWhy = canServe ? null : !adult ? t('character.why.minor', { age: AGE_OF_MAJORITY }) : !c.dubbed && !c.isHead && c.heldTitleIds.length === 0 ? (c.squireXp === null && c.age > SQUIRE_MAX_START_AGE ? t('character.why.notDubbedOld', { age: SQUIRE_MAX_START_AGE }) : t('character.why.notDubbed')) : c.knightOf ? t('character.why.knight') : c.squireOf ? t('character.why.squire') : c.isHead ? t('character.why.isHead') : t('character.why.holdsTitles');
  const headWhy = canHead ? null : !adult ? t('character.why.minor', { age: AGE_OF_MAJORITY }) : c.isHead ? t('character.why.isHead') : t('character.why.headTitles');

  // Deep link (?act=marry|edu|service|squire|guard|leave|head|petition) opens the matching sheet, but only if it is really possible.
  const wantedOk = wanted === 'marry' ? canMarry : wanted === 'edu' ? canEdu : wanted === 'service' ? canServe
    : wanted === 'squire' ? canSquire : wanted === 'guard' ? canSquire : wanted === 'head' ? canHead
    : wanted === 'leave' ? !!(c.knightOf || c.squireOf) : false;
  const [dlg, setDlg] = useState<Dialog>(() => (wantedOk ? (wanted as Dialog) : null));
  useEffect(() => {
    if (!wanted) return;
    if (wanted === 'petition') document.getElementById('petition')?.scrollIntoView({ block: 'center' });
    const next = new URLSearchParams(params);
    next.delete('act');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type Act = { key: string; label: string; hint: string; why: string | null; onClick: () => void; variant?: 'primary' | 'ghost' | 'danger' | 'quiet' };
  const acts: Act[] = [
    { key: 'marry', label: t('character.act.marry'), hint: t('character.actHint.marry'), why: marryWhy, onClick: () => setDlg('marry'), variant: 'primary' },
    { key: 'edu', label: t('character.act.education'), hint: t('character.actHint.education'), why: eduWhy, onClick: () => setDlg('edu') },
    { key: 'service', label: t('character.act.service'), hint: t('character.actHint.service'), why: serveWhy, onClick: () => setDlg('service') },
    { key: 'squire', label: t('character.act.squire'), hint: t('character.actHint.squire'), why: squireWhy, onClick: () => setDlg('squire') },
    { key: 'guard', label: t('character.act.guard'), hint: t('character.actHint.guard'), why: squireWhy, onClick: () => setDlg('guard') },
    ...((c.knightOf || c.squireOf) ? [{ key: 'leave', label: t('character.act.leave'), hint: t('character.actHint.leave'), why: null, onClick: () => setDlg('leave'), variant: 'quiet' as const }] : []),
    { key: 'head', label: t('character.act.head'), hint: t('character.actHint.head'), why: headWhy, onClick: () => setDlg('head') },
  ];
  const available = acts.filter((a) => !a.why);
  const unavailable = acts.filter((a) => a.why);

  return (
    <div className="act-group">
      <p className="act-hint">{t('character.actionsOwnHint', { name: c.firstName })}</p>
      {available.length > 0 && (
        <div className="act-buttons">
          {available.map((a) => <Btn key={a.key} variant={a.variant ?? 'ghost'} title={a.hint} onClick={a.onClick}>{a.label}</Btn>)}
        </div>
      )}
      {c.knightOf && adult && <Petition c={c} />}
      {unavailable.length > 0 && (
        <Collapsible title={t('ui.notAvailable')} summary={unavailable.length} className="flush">
          <ul className="list">
            {unavailable.map((a) => <li key={a.key}><div className="grow"><b>{a.label}</b><span className="lr-sub">{a.why}</span></div></li>)}
          </ul>
        </Collapsible>
      )}

      <Sheet fixed open={dlg === 'marry'} onClose={close} title={t('marry.title', { name: c.firstName })}>
        <MarryForm c={c} house={house} actor={actor} cmd={cmd} onDone={close} />
      </Sheet>
      <Sheet open={dlg === 'edu'} onClose={close} title={t('edu.title', { name: c.firstName })}>
        <EduForm c={c} house={house} actor={actor} cmd={cmd} onDone={close} />
      </Sheet>
      <Sheet fixed open={dlg === 'service'} onClose={close} title={t('service.title', { name: c.firstName })}>
        <ServiceForm c={c} house={house} actor={actor} cmd={cmd} onDone={close} />
      </Sheet>
      <Sheet fixed open={dlg === 'squire'} onClose={close} title={t('squire.title', { name: c.firstName })}>
        <SquireForm c={c} house={house} actor={actor} cmd={cmd} onDone={close} />
      </Sheet>
      <Sheet fixed open={dlg === 'guard'} onClose={close} title={t('squire.guardTitle', { name: c.firstName })}>
        <GuardForm c={c} house={house} actor={actor} cmd={cmd} onDone={close} />
      </Sheet>
      <Sheet open={dlg === 'leave'} onClose={close} title={t('character.act.leave')}>
        <ConfirmRow message={t('service.leaveConfirm', { name: c.firstName })} busy={cmd.pending}
          onConfirm={async () => { const r = await cmd.run({ type: 'leave_service', actorId: actor, knightId: c.id }); if (r.ok) close(); }} onCancel={close} />
        <Inline error={cmd.error} />
      </Sheet>
      <Sheet open={dlg === 'head'} onClose={close} title={t('character.act.head')}>
        <ConfirmRow message={t('head.confirm', { name: c.firstName })} busy={cmd.pending}
          onConfirm={async () => { const r = await cmd.run({ type: 'set_head', actorId: actor, newHeadId: c.id }); if (r.ok) close(); }} onCancel={close} />
        <Inline error={cmd.error} />
      </Sheet>
    </div>
  );
}

type FormProps = { c: CharacterView; house: HouseView; actor: Id; cmd: ReturnType<typeof useCommand>; onDone: () => void };

const MARRY_RANKS = Object.keys(RANK_ORDER) as TitleRank[];

function MarryForm({ c, house, actor, cmd, onDone }: FormProps) {
  const { t } = useTranslation();
  const [target, setTarget] = useState<Id | null>(null);
  const [dowry, setDowry] = useState(0);
  const [matri, setMatri] = useState(false);
  const [showMatri, setShowMatri] = useState(false);
  const [minAge, setMinAge] = useState(String(AGE_OF_MAJORITY));
  const [maxAge, setMaxAge] = useState('');
  const [rank, setRank] = useState<'' | TitleRank>('');
  const [text, setText] = useState('');
  // Debounced copies drive the re-query so typing does not fire a request per keystroke.
  const [ageQ, setAgeQ] = useState({ min: AGE_OF_MAJORITY, max: 0 });
  const [optQ, setOptQ] = useState({ dowry: 0, matri: false });
  useEffect(() => {
    const id = setTimeout(() => setAgeQ({ min: Number(minAge) || 0, max: Number(maxAge) || 0 }), 300);
    return () => clearTimeout(id);
  }, [minAge, maxAge]);
  useEffect(() => {
    const id = setTimeout(() => setOptQ({ dowry, matri }), 300);
    return () => clearTimeout(id);
  }, [dowry, matri]);

  const gold = Math.floor(house.house.resources.gold);
  const candidates = useMarriageCandidates(
    { charId: c.id, dowry: optQ.dowry, matrilineal: optQ.matri, minAge: ageQ.min, maxAge: ageQ.max || undefined },
    true,
  );
  const needle = text.trim().toLowerCase();
  const rows = (candidates.data ?? [])
    .filter((r) => !rank || r.rank === rank)
    .filter((r) => !needle || `${r.name} ${r.houseName}`.toLowerCase().includes(needle));
  // The selection survives re-queries while that person is still listed.
  const picked = (candidates.data ?? []).find((r) => r.charId === target) ?? null;
  const active = (Number(minAge) !== AGE_OF_MAJORITY ? 1 : 0) + (maxAge ? 1 : 0) + (rank ? 1 : 0);
  const settling = candidates.isFetching && !candidates.isLoading;

  return (
    <div className="pk">
      <div className="pk-top">
        <p className="pk-help">{t('marry.help')}</p>
        <FilterBar search={text} onSearch={setText} placeholder={t('marry.searchHint')} activeCount={active}>
          <div className="fbar-ages">
            <label>{t('marry.minAge')}<input className="input" type="number" inputMode="numeric" min={0} value={minAge} onChange={(e) => setMinAge(e.target.value)} /></label>
            <label>{t('marry.maxAge')}<input className="input" type="number" inputMode="numeric" min={0} value={maxAge} onChange={(e) => setMaxAge(e.target.value)} /></label>
          </div>
          <Chips value={rank} onChange={setRank} label={t('marry.rank')}
            options={[{ value: '' as const, label: t('marry.rankAll') }, ...MARRY_RANKS.map((r) => ({ value: r, label: RANK_NAMES[r].title }))]} />
        </FilterBar>
      </div>
      <div className={`pk-list ${settling ? 'stale' : ''}`} role="radiogroup" aria-label={t('marry.candidate')} aria-busy={settling}>
        {candidates.isLoading && <Loading />}
        {candidates.isError && <ErrorBox error={candidates.error} onRetry={() => candidates.refetch()} />}
        {!candidates.isLoading && !candidates.isError && rows.length === 0 && <Empty>{t('marry.noCandidates')}</Empty>}
        {rows.map((r) => (
          <PickRow key={r.charId} selected={target === r.charId} onSelect={() => setTarget(r.charId)}
            profileTo={`/character/${r.charId}`} profileLabel={t('pick.profile', { name: r.name })}>
            <div className="pk-l1">
              <b>{r.titledName}</b>
              <VerdictChip verdict={r.verdict} label={t(`marry.verdict.${r.verdict}`)} />
            </div>
            <div className="pk-l2">
              <HouseBadge houseId={r.houseId} name={r.houseName} />
              <span>{t('common.age', { age: r.age })}</span>
              {r.rank && <span title={r.primaryTitle ?? undefined}>{RANK_NAMES[r.rank].title}</span>}
              <span className={`mood-${moodOf(r.opinionOfUs)}`}>{t(`pick.mood.${moodOf(r.opinionOfUs)}`)}</span>
            </div>
          </PickRow>
        ))}
      </div>
      <div className="pk-foot">
        {picked ? (
          <div className="pk-sum"><b>{picked.titledName}</b><VerdictChip verdict={picked.verdict} label={t(`marry.verdict.${picked.verdict}`)} /></div>
        ) : (
          <div className="pk-sum empty">{t('marry.choose')}</div>
        )}
        <div className="pk-opts">
          <label className="dowry">
            <ResourceIcon k="gold" size={22} />
            <input className="input" type="number" inputMode="numeric" min={0} max={gold} value={dowry} aria-label={t('marry.dowry')}
              onChange={(e) => setDowry(Math.min(gold, Math.max(0, Math.floor(Number(e.target.value) || 0))))} />
            <span className="small muted">{t('marry.dowryHave', { gold })}</span>
          </label>
          <span className="pk-toggle">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={matri} onChange={(e) => setMatri(e.target.checked)} />{t('marry.matrilineal')}
            </label>
            <button type="button" className="info-btn" aria-expanded={showMatri} aria-label={t('marry.matrilinealInfo')} title={t('marry.matrilinealHint')} onClick={() => setShowMatri((v) => !v)}>i</button>
          </span>
        </div>
        {showMatri && <p className="small muted" style={{ margin: 0 }}>{t('marry.matrilinealHint')}</p>}
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!picked} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'propose_marriage', actorId: actor, ourCharId: c.id, theirCharId: picked!.charId, matrilineal: matri, dowry }); if (r.ok) onDone(); }}>
          {t('marry.send')}
        </Btn>
      </div>
    </div>
  );
}

function EduForm({ c, house, actor, cmd, onDone }: FormProps) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState<EducationFocus>((c.education?.focus as EducationFocus | undefined) ?? 'learning');
  const [tutor, setTutor] = useState<string>(c.education?.tutorId ?? '');
  const tutors = house.members.filter((m) => m.alive && m.age >= AGE_OF_MAJORITY && m.id !== c.id);
  return (
    <div>
      <Field label={t('edu.focus')}>
        <select className="input" value={focus} onChange={(e) => setFocus(e.target.value as EducationFocus)}>
          {FOCI.map((f) => <option key={f} value={f}>{t(`attr.${f}`)}</option>)}
        </select>
      </Field>
      <Field label={t('edu.tutor')} hint={t('edu.tutorHint')}>
        <select className="input" value={tutor} onChange={(e) => setTutor(e.target.value)}>
          <option value="">{t('edu.noTutor')}</option>
          {tutors.map((m) => <option key={m.id} value={m.id}>{m.firstName} ({t(`attr.${focus}`)} {m.attributes[focus]})</option>)}
        </select>
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'set_education', actorId: actor, childId: c.id, focus, tutorId: tutor || null }); if (r.ok) onDone(); }}>
          {t('common.save')}
        </Btn>
      </div>
    </div>
  );
}

const NEAR_DAYS = 8;
const SERVICE_RANKS = MARRY_RANKS.slice().sort((x, y) => RANK_ORDER[y] - RANK_ORDER[x]);

function ServiceForm({ c, actor, cmd, onDone }: FormProps) {
  const { t } = useTranslation();
  const cands = useServiceCandidates(c.id, true);
  const [titleId, setTitleId] = useState<Id | null>(null);
  const [rank, setRank] = useState<'' | TitleRank>('');
  const [text, setText] = useState('');
  const [near, setNear] = useState(false);
  const all = cands.data ?? [];
  const needle = text.trim().toLowerCase();
  const rows = all
    .filter((r) => !rank || r.rank === rank)
    .filter((r) => !near || r.ownHouse || (r.distanceDays !== null && r.distanceDays <= NEAR_DAYS))
    .filter((r) => !needle || `${r.holderName} ${r.houseName ?? ''} ${r.titleName}`.toLowerCase().includes(needle));
  const open = rows.filter((r) => r.verdict !== 'full');
  const full = rows.filter((r) => r.verdict === 'full');
  const ranksPresent = SERVICE_RANKS.filter((r) => all.some((x) => x.rank === r));
  const picked = all.find((r) => r.titleId === titleId && r.verdict !== 'full') ?? null;
  const active = (rank ? 1 : 0) + (near ? 1 : 0);

  const row = (r: ServiceCandidate) => {
    const disabled = r.verdict === 'full';
    return (
      <PickRow key={r.titleId} selected={!disabled && titleId === r.titleId} disabled={disabled} className={r.ownHouse ? 'own' : ''}
        onSelect={() => setTitleId(r.titleId)} profileTo={`/character/${r.holderId}`} profileLabel={t('pick.profile', { name: r.holderName })}>
        <div className="pk-l1">
          <b>{r.holderTitledName}</b>
          <VerdictChip verdict={r.verdict} label={t(`service.verdict.${r.verdict}`)} />
        </div>
        <div className="pk-l2">
          {r.houseName && !r.ownHouse && <HouseBadge houseId={r.houseId} name={r.houseName} />}
          <span>{RANK_NAMES[r.rank].title}: {r.titleName}{r.otherTitles > 0 ? ` ${t('service.andMore', { count: r.otherTitles })}` : ''}</span>
          <span>{r.distanceDays === null ? t('service.noRoad') : r.distanceDays === 0 ? t('service.here') : t('service.days', { count: r.distanceDays })}</span>
          <span>{t('service.slots', { n: r.knights, max: r.limit })}</span>
          {!r.ownHouse && r.verdict !== 'full' && <span className={`mood-${moodOf(r.opinionOfKnight)}`}>{t(`pick.mood.${moodOf(r.opinionOfKnight)}`)}</span>}
        </div>
      </PickRow>
    );
  };

  const note = !picked ? null : picked.ownHouse ? t('service.ownNote') : picked.control === 'player' ? t('service.playerNote') : picked.verdict === 'unlikely' ? t('service.unlikelyNote') : null;

  return (
    <div className="pk">
      <div className="pk-top">
        <p className="pk-help">{t('service.help')}</p>
        <FilterBar search={text} onSearch={setText} placeholder={t('service.searchHint')} activeCount={active}>
          <Chips value={rank} onChange={setRank} label={t('service.rank')}
            options={[{ value: '' as const, label: t('marry.rankAll') }, ...ranksPresent.map((r) => ({ value: r, label: RANK_NAMES[r].title }))]} />
          <label className="fbar-near"><input type="checkbox" checked={near} onChange={(e) => setNear(e.target.checked)} />{t('service.near', { days: NEAR_DAYS })}</label>
        </FilterBar>
      </div>
      <div className="pk-list" role="radiogroup" aria-label={t('service.title_')}>
        {cands.isLoading && <Loading />}
        {cands.isError && <ErrorBox error={cands.error} onRetry={() => cands.refetch()} />}
        {!cands.isLoading && !cands.isError && rows.length === 0 && <Empty>{all.length === 0 ? t('service.none') : t('service.noMatch')}</Empty>}
        {open.map(row)}
        {full.length > 0 && (
          <Collapsible title={t('service.fullLords')} summary={full.length} className="flush">
            {full.map(row)}
          </Collapsible>
        )}
      </div>
      <div className="pk-foot">
        {picked ? (
          <div className="pk-sum">
            <b>{picked.holderTitledName}</b>
            <VerdictChip verdict={picked.verdict} label={t(`service.verdict.${picked.verdict}`)} />
            {note && <span className="small muted">{note}</span>}
          </div>
        ) : (
          <div className="pk-sum empty">{t('service.choose')}</div>
        )}
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!picked} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'offer_service', actorId: actor, knightId: c.id, titleId: titleId! }); if (r.ok) onDone(); }}>
          {picked ? (picked.ownHouse ? t('service.swear', { name: picked.holderTitledName }) : t('service.offerTo', { name: picked.holderTitledName })) : t('service.offer')}
        </Btn>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ chivalry

/** Dubbing, squire training, the master or guard a squire serves, a knight's squires and captaincy. */
function ChivalrySection({ c }: { c: CharacterView }) {
  const { t } = useTranslation();
  const training = c.squireXp !== null;
  const tutor = useCharacter(c.alive && c.age < 16 ? c.education?.tutorId : null);
  const guardian = tutor.data && tutor.data.alive && tutor.data.houseId !== c.houseId ? tutor.data : null; // fostered in another house
  const shown = c.dubbed || training || c.squireIds.length > 0 || !!c.guardCaptainOf || !!guardian;
  if (!shown) return null;
  const xp = c.squireXp ?? 0;
  return (
    <div className="chiv">
      <h3 className="hist-sub">{t('character.chivalry')}</h3>
      <div className="rel-list">
        {guardian && <div className="kv"><span>{t('chivalry.wardOf')}</span><CharLink id={guardian.id} /></div>}
        <div className="kv"><span>{t('chivalry.dubbed')}</span>
          <span>{c.dubbed && c.knightTier ? <Tag tone="gold">{t(`chivalry.tier.${c.knightTier}`)}</Tag> : <span className="muted">{t('chivalry.notDubbed')}</span>}</span>
        </div>
        {training && (
          <div className="chiv-xp">
            <div className="chiv-xp-head"><span>{t('chivalry.training')}: {t(`chivalry.rank.${c.squireRank ?? 'novice'}`)}</span><span className="num">{xp} / {SQUIRE_XP_MAX}</span></div>
            <div className="chiv-xp-track" role="progressbar" aria-label={t('chivalry.training')} aria-valuemin={0} aria-valuemax={SQUIRE_XP_MAX} aria-valuenow={xp}>
              <span className="chiv-xp-fill" style={{ width: `${xp}%` }} />
              <span className="chiv-tick" style={{ left: '33%' }} aria-hidden="true" />
              <span className="chiv-tick" style={{ left: '66%' }} aria-hidden="true" />
            </div>
          </div>
        )}
        {(training || c.squireMasterId || c.guardOf) && (
          <div className="kv"><span>{t('chivalry.master')}</span>
            {c.squireMasterId ? <CharLink id={c.squireMasterId} />
              : c.guardOf ? <span>{t('chivalry.guard')} <TitleLink id={c.guardOf} /></span>
              : <span className="muted">{t('chivalry.unplaced')}</span>}
          </div>
        )}
        {(c.dubbed || c.squireIds.length > 0) && (
          <div className="kv"><span>{t('chivalry.squires')} <span className="muted small">{t('chivalry.capacity', { n: c.squireIds.length, max: c.squireCapacity })}</span></span>
            <span style={{ textAlign: 'right' }}>{c.squireIds.length === 0 ? <span className="muted">{t('chivalry.noSquires')}</span>
              : c.squireIds.map((sid, i) => <span key={sid}>{i > 0 && ', '}<CharLink id={sid} /></span>)}</span>
          </div>
        )}
        {c.guardCaptainOf && (
          <div className="kv"><span>{t('chivalry.captain')}</span><TitleLink id={c.guardCaptainOf} /></div>
        )}
        <div className="kv"><span>{t('chivalry.standing')}</span><span className="num">{Math.round(c.standing)}</span></div>
      </div>
    </div>
  );
}

/** Release a squire from the post this house controls: the master's house, or the holder of the guarded title. */
function DismissSquire({ c, house }: { c: CharacterView; house: HouseView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [open, setOpen] = useState(false);
  const guardTitle = useTitle(c.squireMasterId ? null : c.guardOf);
  if (!c.squireOf) return null;
  const members = new Set(house.members.map((m) => m.id));
  const ours = c.squireMasterId ? members.has(c.squireMasterId) : !!guardTitle.data?.holderId && members.has(guardTitle.data.holderId);
  if (!ours) return null;
  const close = () => { setOpen(false); cmd.clearError(); };
  return (
    <div className="act-group">
      <p className="act-hint">{t('character.actHint.dismiss')}</p>
      <div className="act-buttons">
        <Btn variant="quiet" onClick={() => setOpen(true)}>{t('character.act.dismiss')}</Btn>
      </div>
      <Sheet open={open} onClose={close} title={t('character.act.dismiss')}>
        <ConfirmRow message={t('squire.dismissConfirm', { name: c.firstName })} busy={cmd.pending}
          onConfirm={async () => { const r = await cmd.run({ type: 'dismiss_squire', actorId: actorOf(house), squireId: c.id }); if (r.ok) close(); }}
          onCancel={close} />
        <Inline error={cmd.error} />
      </Sheet>
    </div>
  );
}

// ------------------------------------------------------------------ squire & guard placement

function SquireForm({ c, actor, cmd, onDone }: FormProps) {
  const { t } = useTranslation();
  const cands = useSquireCandidates(c.id, true);
  const [knightId, setKnightId] = useState<Id | null>(null);
  const [text, setText] = useState('');
  const [near, setNear] = useState(false);
  const all = cands.data ?? [];
  const needle = text.trim().toLowerCase();
  const rows = all
    .filter((r) => !near || r.verdict === 'own' || (r.distanceDays !== null && r.distanceDays <= NEAR_DAYS))
    .filter((r) => !needle || `${r.knightName} ${r.houseName ?? ''} ${r.courtName ?? ''}`.toLowerCase().includes(needle));
  const open = rows.filter((r) => r.verdict !== 'full');
  const full = rows.filter((r) => r.verdict === 'full');
  const picked = all.find((r) => r.knightId === knightId && r.verdict !== 'full') ?? null;
  const active = near ? 1 : 0;

  const row = (r: SquireCandidate) => {
    const disabled = r.verdict === 'full';
    return (
      <PickRow key={r.knightId} selected={!disabled && knightId === r.knightId} disabled={disabled} className={r.verdict === 'own' ? 'own' : ''}
        onSelect={() => setKnightId(r.knightId)} profileTo={`/character/${r.knightId}`} profileLabel={t('pick.profile', { name: r.knightName })}>
        <div className="pk-l1">
          <b>{r.knightName}</b>
          <VerdictChip verdict={r.verdict} label={t(`squire.verdict.${r.verdict}`)} />
        </div>
        <div className="pk-l2">
          {r.houseName && r.verdict !== 'own' && <HouseBadge houseId={r.houseId} name={r.houseName} />}
          {r.courtName && <span>{r.courtName}</span>}
          <span>{r.distanceDays === null ? t('service.noRoad') : r.distanceDays === 0 ? t('service.here') : t('service.days', { count: r.distanceDays })}</span>
          <span>{t('squire.slots', { n: r.squires, max: r.capacity })}</span>
          <span>{t('squire.standing', { n: Math.round(r.standing) })}</span>
          {r.verdict !== 'own' && !disabled && <span className={`mood-${moodOf(r.opinion)}`}>{t(`pick.mood.${moodOf(r.opinion)}`)}</span>}
        </div>
      </PickRow>
    );
  };

  const note = !picked ? null : picked.verdict === 'own' ? t('squire.ownNote') : picked.control === 'player' ? t('squire.playerNote') : picked.verdict === 'unlikely' ? t('squire.unlikelyNote') : null;

  return (
    <div className="pk">
      <div className="pk-top">
        <p className="pk-help">{t('squire.help')}</p>
        <FilterBar search={text} onSearch={setText} placeholder={t('squire.searchHint')} activeCount={active}>
          <label className="fbar-near"><input type="checkbox" checked={near} onChange={(e) => setNear(e.target.checked)} />{t('squire.near', { days: NEAR_DAYS })}</label>
        </FilterBar>
      </div>
      <div className="pk-list" role="radiogroup" aria-label={t('squire.choose')}>
        {cands.isLoading && <Loading />}
        {cands.isError && <ErrorBox error={cands.error} onRetry={() => cands.refetch()} />}
        {!cands.isLoading && !cands.isError && rows.length === 0 && <Empty>{all.length === 0 ? t('squire.none') : t('squire.noMatch')}</Empty>}
        {open.map(row)}
        {full.length > 0 && (
          <Collapsible title={t('squire.full')} summary={full.length} className="flush">
            {full.map(row)}
          </Collapsible>
        )}
      </div>
      <div className="pk-foot">
        {picked ? (
          <div className="pk-sum">
            <b>{picked.knightName}</b>
            <VerdictChip verdict={picked.verdict} label={t(`squire.verdict.${picked.verdict}`)} />
            {note && <span className="small muted">{note}</span>}
          </div>
        ) : (
          <div className="pk-sum empty">{t('squire.choose')}</div>
        )}
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!picked} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'send_squire', actorId: actor, squireId: c.id, knightId: knightId! }); if (r.ok) onDone(); }}>
          {picked ? t('squire.sendTo', { name: picked.knightName }) : t('squire.send')}
        </Btn>
      </div>
    </div>
  );
}

/** Titles of our house (and their liege chain) that keep a Guard; cities keep none. */
function GuardForm({ c, house, actor, cmd, onDone }: FormProps) {
  const { t } = useTranslation();
  const index = useTitleIndex();
  const titles = useMemo(() => {
    const ids = new Set<Id>();
    for (const m of house.members) {
      if (!m.alive) continue;
      for (const tid of m.heldTitleIds) {
        let cur: Id | null = tid;
        while (cur && !ids.has(cur)) {
          ids.add(cur);
          cur = index.get(cur)?.liegeTitleId ?? null;
        }
      }
    }
    return [...ids].map((id) => index.get(id)).filter((x): x is TitleSummary => !!x && x.rank !== 'city')
      .sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank] || a.name.localeCompare(b.name));
  }, [house, index]);

  return (
    <div className="pk">
      <div className="pk-top"><p className="pk-help">{t('squire.guardHelp')}</p></div>
      <div className="pk-list">
        {titles.length === 0 && <Empty>{t('common.none')}</Empty>}
        <ul className="list">
          {titles.map((tt) => <GuardRow key={tt.id} title={tt} squire={c} actor={actor} cmd={cmd} onDone={onDone} />)}
        </ul>
      </div>
      <Inline error={cmd.error} />
    </div>
  );
}

function GuardRow({ title, squire, actor, cmd, onDone }: { title: TitleSummary; squire: CharacterView; actor: Id; cmd: ReturnType<typeof useCommand>; onDone: () => void }) {
  const { t } = useTranslation();
  const tv = useTitle(title.id);
  const n = tv.data?.guardIds.length ?? 0;
  const max = tv.data?.guardLimit ?? 0;
  const full = !tv.data || max === 0 || n >= max;
  return (
    <li>
      <div className="grow">
        <b>{title.name}</b>
        <span className="lr-sub">{tv.isError ? t('error.generic') : tv.data ? t('squire.guardSlots', { n, max }) : t('common.loading')}{full && tv.data ? ` · ${t('squire.guardFull')}` : ''}</span>
      </div>
      <Btn small disabled={full} busy={cmd.pending}
        onClick={async () => { const r = await cmd.run({ type: 'join_guard', actorId: actor, squireId: squire.id, titleId: title.id }); if (r.ok) onDone(); }}>
        {t('squire.guardJoin', { name: title.name })}
      </Btn>
    </li>
  );
}

function Petition({ c }: { c: CharacterView }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [sent, setSent] = useState<'land' | 'council' | null>(null);
  const [info, setInfo] = useState(false);
  const ask = async (kind: 'land' | 'council') => {
    setSent(null);
    cmd.clearError();
    const r = await cmd.run({ type: 'petition_liege', actorId: c.id, kind });
    if (r.ok) setSent(kind);
  };
  return (
    <div className="act-group" id="petition">
      <h3 className="hist-sub pet-head"><IconHourglass size={18} /> <span>{t('petition.title')}</span>
        <button type="button" className="info-btn" aria-expanded={info} aria-label={t('petition.title')} onClick={() => setInfo((v) => !v)}>i</button>
      </h3>
      {info && <p className="act-hint">{t('petition.help')}</p>}
      <div className="act-buttons">
        <Btn onClick={() => ask('land')} busy={cmd.pending}>{t('petition.land')}</Btn>
        <Btn onClick={() => ask('council')} busy={cmd.pending}>{t('petition.council')}</Btn>
      </div>
      <Inline error={cmd.error} />
      {sent && <p className="small muted">{t(`petition.sent.${sent}`)}</p>}
    </div>
  );
}
